/**
 * Widget Configuration Page
 *
 * Complete provider widget configuration interface with tabbed navigation.
 * Allows providers to customize branding, services, pricing, and embed settings.
 */

import * as React from 'react';
import { PageHeader } from '@/components/service-provider/PageHeader';
import { useWidgetConfig } from '@/hooks/useWidgetConfig';
import { OverviewTab } from '@/components/widget-config/OverviewTab';
import { BrandingTab } from '@/components/widget-config/BrandingTab';
import { ServicesTab } from '@/components/widget-config/ServicesTab';
import { PricingTab } from '@/components/widget-config/PricingTab';
import { BusinessInfoTab } from '@/components/widget-config/BusinessInfoTab';
import { EmbedCodeTab } from '@/components/widget-config/EmbedCodeTab';

type TabId = 'overview' | 'branding' | 'services' | 'pricing' | 'business-info' | 'embed-code';

interface Tab {
  id: TabId;
  label: string;
  icon: React.ReactNode;
}

const tabs: Tab[] = [
  {
    id: 'overview',
    label: 'Overview',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
        />
      </svg>
    ),
  },
  {
    id: 'branding',
    label: 'Branding',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
        />
      </svg>
    ),
  },
  {
    id: 'services',
    label: 'Services',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
        />
      </svg>
    ),
  },
  {
    id: 'pricing',
    label: 'Pricing',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  {
    id: 'business-info',
    label: 'Business Info',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
        />
      </svg>
    ),
  },
  {
    id: 'embed-code',
    label: 'Embed Code',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
        />
      </svg>
    ),
  },
];

export function WidgetConfigPage() {
  const [activeTab, setActiveTab] = React.useState<TabId>('overview');
  const { data: config, isLoading, error } = useWidgetConfig();

  // Loading State
  if (isLoading) {
    return (
      <div>
        <PageHeader
          title="Widget Configuration"
          description="Configure your embedded booking widget"
          breadcrumbs={[
            { label: 'Dashboard', href: '/provider/dashboard' },
            { label: 'Widget Config' },
          ]}
        />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
            <p className="mt-4 text-sm text-gray-600">Loading configuration...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div>
        <PageHeader
          title="Widget Configuration"
          description="Configure your embedded booking widget"
          breadcrumbs={[
            { label: 'Dashboard', href: '/provider/dashboard' },
            { label: 'Widget Config' },
          ]}
        />
        <div className="mx-4 md:mx-6 lg:mx-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <svg
                className="w-6 h-6 text-red-600 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <h3 className="text-sm font-semibold text-red-900">
                  Failed to load configuration
                </h3>
                <p className="mt-1 text-sm text-red-700">
                  {error instanceof Error ? error.message : 'An unexpected error occurred'}
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-3 text-sm text-red-600 hover:text-red-700 underline"
                >
                  Reload page
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No config state (shouldn't happen, but handle it)
  if (!config) {
    return (
      <div>
        <PageHeader
          title="Widget Configuration"
          description="Configure your embedded booking widget"
          breadcrumbs={[
            { label: 'Dashboard', href: '/provider/dashboard' },
            { label: 'Widget Config' },
          ]}
        />
        <div className="mx-4 md:mx-6 lg:mx-8">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <p className="text-sm text-yellow-800">
              No configuration found. Please contact support.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Widget Configuration"
        description="Configure your embedded booking widget"
        breadcrumbs={[
          { label: 'Dashboard', href: '/provider/dashboard' },
          { label: 'Widget Config' },
        ]}
      />

      <div className="px-4 md:px-6 lg:px-8 pb-8">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8 overflow-x-auto" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors
                  ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
                aria-current={activeTab === tab.id ? 'page' : undefined}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          {activeTab === 'overview' && <OverviewTab config={config} />}
          {activeTab === 'branding' && <BrandingTab config={config} />}
          {activeTab === 'services' && <ServicesTab config={config} />}
          {activeTab === 'pricing' && <PricingTab config={config} />}
          {activeTab === 'business-info' && <BusinessInfoTab config={config} />}
          {activeTab === 'embed-code' && <EmbedCodeTab config={config} />}
        </div>
      </div>
    </div>
  );
}
